/**
 * `<VmStatusPill>` — user-facing cloud-VM connection-state pill
 * (cloud-VM supportability TDD §A-1).
 *
 * Subscribes to the `vm:connection-state` push channel and renders a
 * small coloured pill in the Foundry workspace chrome reflecting the
 * five derived states (`idle` / `connecting` / `connected` /
 * `reconnecting` / `unreachable`). The 8-row priority derivation lives
 * in `main/vm-connection-state.ts`; the renderer NEVER re-derives — it
 * only renders the state main sent.
 *
 * VM-flag-OFF safety: the component returns `null` before mounting an
 * IPC subscription when the cloud-VM flag is OFF. The producer side
 * also gates the push channel so the renderer would never receive an
 * event anyway, but the early-return is the load-bearing defence-in-
 * depth — a stray push (or a future flag flip racing the renderer
 * mount) cannot leak the pill into the chrome of a flag-OFF Foundry.
 *
 * Pre-event state: when the flag is ON but no push has been received
 * yet (cold start before the producer dispatches its first transition),
 * the pill renders `null` so the chrome doesn't flash a placeholder.
 * The component re-renders once the first push arrives.
 *
 * Visual spec (TDD §A-1 pill visual table):
 *
 *   | State          | Glyph | Colour       |
 *   |---------------:|:-----:|:-------------|
 *   | idle           |   ⚪  | neutral-gray |
 *   | connecting     |   🟡  | amber        |
 *   | connected      |   🟢  | green        |
 *   | reconnecting   |   🟠  | orange       |
 *   | unreachable    |   🔴  | red          |
 *
 * Tooltip copy (TDD §A-1):
 *
 *   - idle         → "VM idle — no session active"
 *   - connecting   → "Connecting to VM…"
 *   - connected    → "VM connected (last heartbeat <ms>ms ago)"
 *   - reconnecting → "Reconnecting to VM…"
 *   - unreachable  → "VM unreachable — check Settings → Diagnostics"
 *
 * British spelling in code comments; user-facing copy is verbatim from
 * the TDD §A-1 table.
 */

import { useEffect, useState } from 'react';
import type {
  VmConnectionState,
  VmConnectionStatePush,
} from '@volley/crucible-ipc-types/vm-connection-state';

interface PillVisual {
  glyph: string;
  /** Tailwind utility classes for the pill's background + text +
   *  border. Token family per TDD §A-1 visual table — distinct from
   *  the existing AgentActivity / Deploy pills so the cloud-VM state
   *  doesn't collide visually with chat / build affordances. */
  className: string;
  /** Static label rendered after the glyph. */
  label: string;
}

const PILL_VISUALS: Record<VmConnectionState, PillVisual> = {
  idle: {
    glyph: '⚪',
    className: 'bg-white/5 text-white/50 border border-white/10',
    label: 'VM idle',
  },
  connecting: {
    glyph: '🟡',
    className: 'bg-amber-500/15 text-amber-200/90 border border-amber-500/30',
    label: 'Connecting',
  },
  connected: {
    glyph: '🟢',
    className: 'bg-emerald-500/15 text-emerald-200/90 border border-emerald-500/30',
    label: 'VM connected',
  },
  reconnecting: {
    glyph: '🟠',
    className: 'bg-orange-500/15 text-orange-200/90 border border-orange-500/30',
    label: 'Reconnecting',
  },
  unreachable: {
    glyph: '🔴',
    className: 'bg-red-500/15 text-red-200/90 border border-red-500/30',
    label: 'VM unreachable',
  },
};

function tooltipFor(payload: VmConnectionStatePush): string {
  switch (payload.state) {
    case 'idle':
      return 'VM idle — no session active';
    case 'connecting':
      return 'Connecting to VM…';
    case 'connected': {
      // `lastSuccessfulRoundTripAgoMs` may be `null` even in the
      // connected state if the producer pushes a transition before
      // it records its first RTT. The TDD priority table actually
      // routes "open + RTT === null" to `connecting` (priority 5),
      // so seeing `null` here would be unusual — but defensively
      // fall back to a no-RTT tooltip rather than rendering "null".
      const rtt = payload.lastSuccessfulRoundTripAgoMs;
      if (rtt === null) return 'VM connected';
      return `VM connected (last heartbeat ${rtt}ms ago)`;
    }
    case 'reconnecting':
      return 'Reconnecting to VM…';
    case 'unreachable':
      return 'VM unreachable — check Settings → Diagnostics';
  }
}

export interface VmStatusPillProps {
  /** Optional extra class names spliced onto the outer pill. Mirrors
   *  the `<ReadyIndicator>` passthrough convention — caller owns
   *  spacing / margin, component owns colour / shape. */
  className?: string;
}

/**
 * The pill. Subscribes to `vm:connection-state` on mount, unsubscribes
 * on unmount. State is stored as the latest-seen `VmConnectionStatePush`
 * (or `null` until the first push lands).
 */
export function VmStatusPill({
  className = '',
}: VmStatusPillProps): React.JSX.Element | null {
  const [payload, setPayload] = useState<VmConnectionStatePush | null>(null);

  // The cloud-VM feature-flag gate is the PARENT's responsibility
  // (Workspace.tsx mounts this component conditionally on
  // `flags.cloudVmV1`). Pulling useFeatureFlags into this component
  // would force every SSR test that renders WorkspaceHeader to wrap
  // in a QueryClientProvider; pushing the gate to the call site keeps
  // VmStatusPill SSR-friendly. Defence-in-depth: the producer-side
  // gate at main/index.ts means a stray push can't reach here when
  // the flag is OFF anyway, so the only thing this component needs
  // to defend against is the "subscribed but no event yet" case.
  useEffect(() => {
    // SSR / test environments may not stub the bridge; bail rather
    // than throw.
    if (typeof window === 'undefined' || !window.crucible) return;

    const unsubscribe = window.crucible.on(
      'vm:connection-state',
      (push: VmConnectionStatePush) => {
        setPayload(push);
      },
    );
    return () => {
      unsubscribe();
    };
  }, []);

  // Pre-event: render nothing. The pill MUST never flash before main
  // dispatches its first transition; we'd rather have the chrome show
  // no pill than a placeholder.
  if (payload === null) return null;

  const visual = PILL_VISUALS[payload.state];

  return (
    <span
      data-testid="vm-status-pill"
      data-vm-state={payload.state}
      title={tooltipFor(payload)}
      // Same shape token family as the existing chrome pills
      // (`rounded-full px-2.5 py-0.5 text-xs font-medium`), with the
      // state-specific colour tokens spliced in.
      className={`mr-2 inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${visual.className} ${className}`.trim()}
    >
      <span aria-hidden="true" data-testid="vm-status-pill-glyph">
        {visual.glyph}
      </span>
      <span data-testid="vm-status-pill-label">{visual.label}</span>
    </span>
  );
}
